import { CloseOutlined } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Icon,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import Fields from '../../Common/Fields'
import {
    fetchBusinessRulesAttributes,
    fetchBusinessRulesDataByID,
} from '../../../services/dtServices'
import { updateAttributeValues } from '../../../utils/EntityAttributesUtils'
import { store } from '../../../redux/store'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import {
    AttributeType,
    BusinessRulesAttributes,
} from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import {
    fieldsToUpdateErrorMessage,
    handleUpdateRulesFields,
    UpdateErrorMeassagesRulesFields,
} from './HelperFunctions/handleUpdateRulesFields'
import { validateRulesFields } from './HelperFunctions/validateRulesFields'
import PaperComponent from '../../Common/PaperComponent'

interface RuelInstanceProps {
    instanceId: string
    createTemplateId: string
    openFields: any
    handleCloseFields: any
    onSave: any
    isPreview: boolean
    ruleTypeName: string
}

const RuelInstance = (props: RuelInstanceProps): ReactElement => {
    const {
        instanceId,
        createTemplateId,
        openFields,
        handleCloseFields,
        onSave,
        isPreview,
        ruleTypeName,
    } = props
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)

    const dispatch = useAppDispatch()

    const [rulesFields, setRulesFields] = useState<any[]>([])

    useEffect(() => {
        if (openFields) {
            dispatch(updateLoader(true))
            const getRulesAttributes = async () => {
                let updatedRulesFields = []
                if (instanceId && createTemplateId) {
                    updatedRulesFields = await fetchBusinessRulesDataByID(
                        instanceId,
                        org?.id || '',
                    )
                } else if (createTemplateId) {
                    const getAttributesRes: any =
                        await fetchBusinessRulesAttributes(
                            org?.id || '',
                            createTemplateId,
                        )

                    updatedRulesFields = getAttributesRes?.attributes?.map(
                        (i: any) => {
                            return {
                                ...i,
                                value: i.value ? i.value : i.defaultValue || '',
                            }
                        },
                    )
                } else {
                    updatedRulesFields = []
                }
                if (updatedRulesFields?.length) {
                    updatedRulesFields = updateAttributeValues(
                        updatedRulesFields,
                        store,
                        !!instanceId,
                    )
                    updatedRulesFields = updatedRulesFields.map((i: any) => {
                        if (
                            i.id.toLowerCase() ===
                            BusinessRulesAttributes.TARGET
                        ) {
                            let updatedValue: any = ''
                            try {
                                updatedValue = JSON.parse(i.value)
                            } catch (e) {
                                console.log(e)
                            }
                            return {
                                ...i,
                                value: updatedValue,
                                dataType: AttributeType.RULES_FIELD_TRIGGER,
                            }
                        } else if (
                            i.id.toLowerCase() ===
                            BusinessRulesAttributes.SCRIPT
                        ) {
                            return {
                                ...i,
                                dataType: AttributeType.TEXT_AREA,
                                width: 'calc(100% - 20px)',
                            }
                        } else {
                            return { ...i }
                        }
                    })
                    updatedRulesFields =
                        handleUpdateRulesFields(updatedRulesFields)
                    setRulesFields(updatedRulesFields)
                } else {
                    setRulesFields([])
                }
                dispatch(updateLoader(false))
            }

            getRulesAttributes()
        } else {
            setRulesFields([])
        }
    }, [openFields, instanceId, createTemplateId, org?.id, dispatch])

    const onChangeFields = (
        id: string,
        value: any,
        key?: string,
        triggerVal?: boolean,
    ) => {
        let updateRulesFields = [...rulesFields]
        updateRulesFields = updateRulesFields.map((item: any) => {
            if (id.toLowerCase() === BusinessRulesAttributes.TARGET) {
                if (item.id.toLowerCase() === BusinessRulesAttributes.TARGET) {
                    return {
                        ...item,
                        errorMessage: '',
                        isRelationAttribute: triggerVal,
                        value,
                    }
                } else if (
                    item.id.toLowerCase() ===
                    BusinessRulesAttributes.TARGETDISPLAYNAME
                ) {
                    return {
                        ...item,
                        errorMessage: '',
                        value: value?.displayName || '',
                    }
                } else if (
                    item.dataType.toLowerCase() === AttributeType.THRESHOLD
                ) {
                    return {
                        ...item,
                        unit: value?.units,
                    }
                } else {
                    return { ...item }
                }
            } else if (item.id === id) {
                const obj = { ...item }
                obj[key ?? 'value'] = value
                obj['errorMessage'] = ''
                return obj
            } else {
                return { ...item }
            }
        })
        if (id.toLowerCase() === BusinessRulesAttributes.TRIGGER_METHOD) {
            updateRulesFields = handleUpdateRulesFields(updateRulesFields)
        } else if (
            id.toLowerCase() === BusinessRulesAttributes.AGGREGATE_LOW_HIGH
        ) {
            updateRulesFields = handleUpdateRulesFields(updateRulesFields, true)
        }
        if (fieldsToUpdateErrorMessage.includes(id.toLowerCase())) {
            updateRulesFields =
                UpdateErrorMeassagesRulesFields(updateRulesFields)
        }
        setRulesFields(updateRulesFields)
    }

    const handleOnSave = () => {
        const { updatedRulesFields, isValidated } =
            validateRulesFields(rulesFields)

        if (isValidated) {
            const saveRulesFields = updatedRulesFields?.map((i) => {
                if (i.id.toLowerCase() === BusinessRulesAttributes.TARGET) {
                    return { ...i, value: JSON.stringify(i.value) }
                } else {
                    return { ...i }
                }
            })
            onSave(saveRulesFields)
        } else {
            setRulesFields(updatedRulesFields)
        }
    }

    return (
        <Dialog
            open={openFields}
            scroll="paper"
            PaperProps={{
                sx: {
                    maxWidth: '100%',
                    minWidth: 'fit-content',
                    display: 'block',
                },
            }}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            sx={{ padding: '10px 30px' }}
            PaperComponent={PaperComponent}
        >
            <Box
                sx={{
                    padding: '10px 30px',
                    width: {
                        md: '60vw',
                        lg: '55vw',
                        xl: '50vw',
                    },
                }}
            >
                <DialogTitle id="scroll-dialog-title" sx={{ padding: '0px' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            right: '10px',
                            top: '15px',
                            cursor: 'pointer',
                        }}
                    >
                        <Icon
                            id="close-rule-instance-button"
                            onClick={() => handleCloseFields(true)}
                            sx={{ marginRight: '8px' }}
                            fontSize="small"
                            aria-label={t('close', 'Close')}
                            title={t('close', 'Close')}
                        >
                            <CloseOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                    </Box>
                    <Box
                        sx={{
                            marginBottom: '20px',
                            marginTop: '30px',
                            cursor: 'move',
                        }}
                        id="draggable-dialog-title"
                    >
                        {/* <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#000',
                            }}
                        >
                            {rulesFields?.length
                                ? isPreview
                                    ? 'View Rule'
                                    : instanceId && createTemplateId
                                      ? 'Edit Rule'
                                      : 'Add Rule'
                                : ''}
                        </Typography> */}
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#000',
                            }}
                        >
                            {ruleTypeName}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        height: 'calc(100vh - 425px)',
                        padding: '0px',
                    }}
                >
                    <Box>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                        >
                            <Fields
                                fields={rulesFields}
                                onChangeFields={onChangeFields}
                                onClickMap={() => {}}
                                disabled={isPreview}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '15px 0px',
                        paddingRight: '22px',
                    }}
                >
                    {rulesFields?.length ? (
                        <>
                            <Button
                                id="cancel-rule-instance-button"
                                onClick={() => handleCloseFields()}
                                sx={{
                                    fontSize: '12px',
                                    padding: '7px 20px',
                                    marginRight: '15px',
                                }}
                                variant="outlined"
                                title={t('cancel')}
                            >
                                {t('cancel')}
                            </Button>
                            {isPreview ? (
                                <> </>
                            ) : (
                                <Button
                                    id="save-rule-instance-button"
                                    variant="contained"
                                    onClick={() => {
                                        handleOnSave()
                                    }}
                                    sx={{
                                        width: '100px',
                                        color: '#fff',
                                        fontSize: '12px',
                                        padding: '7px 20px',
                                    }}
                                    title={instanceId ? t('update') : t('save')}
                                >
                                    {instanceId ? t('update') : t('save')}
                                </Button>
                            )}
                        </>
                    ) : (
                        <> </>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    )
}
export default RuelInstance
